import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cx } from '../lib/cx';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  closeLabel: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/** Sve što tipkovnica može fokusirati unutar panela, u redoslijedu dokumenta. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  closeLabel,
  children,
  footer,
  className,
}: ModalProps) {
  const id = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * `onClose` je gotovo uvijek inline funkcija, pa bi kao ovisnost efekta
   * vratila fokus na panel nakon SVAKOG pritiska tipke i pojela unos u polju.
   * Zato ide kroz ref — efekt ovisi samo o `open`.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    /**
     * Element s kojeg je dijalog otvoren. Nakon zatvaranja fokus se vraća točno
     * na njega — inače korisnik tipkovnice ispadne na `<body>` i tabulira cijeli
     * ekran ispočetka.
     */
    const opener = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeRef.current();
        return;
      }
      // Zamka fokusa: Tab iz zadnjeg elementa vodi na prvi i obratno. Bez toga
      // tabulator ispuže iza dijaloga u sadržaj koji je vizualno zaključan.
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const active = document.activeElement;
      if (!panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-inverse/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        tabIndex={-1}
        className={cx(
          'relative z-10 w-full max-w-narrow rounded-t-2xl bg-surface p-6 shadow-panel',
          'sm:rounded-2xl max-h-[90dvh] overflow-y-auto safe-bottom',
          className,
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={`${id}-title`} className="font-display text-[24px] font-semibold text-ink">
            {title}
          </h2>
          {/* Meta od 24 px u obje osi — prst na telefonu ne pogađa 16 px križić. */}
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="-mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[20px] leading-none text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        {children}
        {footer && <div className="mt-6 flex flex-wrap justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}
